import React from 'react';
import clsx from 'clsx';
import { useIntl, type IntlShape } from 'react-intl';
import type { DateRedoState } from 'fym-shared/src/music24/dateRedo';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { RedoEnd, RedoWorking } from '../coverRedo/RedoRunBanner';
import { coverRedoMessages } from '../coverRedo/coverRedoMessages';
import type { DateRedo } from './useDateRedo';
import { dateRedoMessages as messages } from './dateRedoMessages';
/* eslint-disable css-modules/no-unused-class -- the stylesheet of the cover redo; the date redo uses its banner slice */
import styles from '../coverRedo/CoverRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DateRedoBannerProps {
  redo: DateRedo;
  onRedo: () => void;
  /** The largest `etaSec` of the kit assets that are being made now, or null. */
  etaSec?: number | null;
}

/** The old → new dates of the banner ("1 Nov → 15 Oct"), or null. Pure. */
export function dateChangeText(intl: IntlShape, state: DateRedoState | undefined): string | null {
  const [old] = state?.oldDateLabels ?? [];
  if (state === undefined || old === undefined) {
    return null;
  }
  return intl.formatMessage(messages.dateChange, {
    old,
    new: state.newDateLabel ?? intl.formatMessage(messages.noDate),
  });
}

/** The offer: the count of the files with the old date, old → new, and the free redo. */
const Offer: React.FC<DateRedoBannerProps> = ({ redo, onRedo }) => {
  const intl = useIntl();
  const { state } = redo;
  const change = dateChangeText(intl, state);
  if (state === undefined) {
    return null;
  }
  return (
    <section className={styles.banner} data-testid="date-redo-banner">
      <div className={styles.body}>
        <h3 className={styles.title}>{intl.formatMessage(messages.bannerTitle, { count: state.total })}</h3>
        <p className={styles.sub}>{intl.formatMessage(messages.bannerBody)}</p>
        {change !== null && (
          <ul className={styles.kinds}>
            <li className={styles.kind}>{change}</li>
          </ul>
        )}
      </div>
      <div className={styles.actions}>
        <div className={styles.buttons}>
          <SMLButton onClick={redo.notNow}>{intl.formatMessage(coverRedoMessages.notNow)}</SMLButton>
          <SMLButton variant="primary" onClick={onRedo}>
            {intl.formatMessage(messages.redo, { count: state.total })}
          </SMLButton>
        </div>
        <span className={clsx(styles.cost, styles.costFree)}>{intl.formatMessage(messages.free)}</span>
      </div>
    </section>
  );
};

/** The one line that stays after "Not now". */
const Quiet: React.FC<DateRedoBannerProps> = ({ redo, onRedo }) => {
  const intl = useIntl();
  return (
    <div className={styles.quiet} data-testid="date-redo-quiet">
      <span>{intl.formatMessage(messages.quietLine, { count: redo.state?.total ?? 0 })}</span>
      <button type="button" className={styles.link} onClick={onRedo}>
        {intl.formatMessage(messages.quietRedo)}
      </button>
    </div>
  );
};

/**
 * The date redo banner of the Kit and the Posts tab: the offer, the progress of a run, its end
 * (the cards of the cover redo, `RedoRunBanner`), or the quiet line after "Not now". Nothing while
 * the kit shows the release date of the song.
 */
const DateRedoBannerComponent: React.FC<DateRedoBannerProps> = ({ redo, onRedo, etaSec }) => {
  const run = redo.state?.run ?? null;
  switch (redo.banner) {
    case 'offer':
      return <Offer redo={redo} onRedo={onRedo} />;
    case 'working':
      return run === null ? null : (
        <RedoWorking
          run={run}
          postsCount={redo.state?.scheduledPosts.length ?? 0}
          etaSec={etaSec}
          title={messages.progressTitle}
          testIdPrefix="date-redo"
        />
      );
    case 'done':
    case 'failed':
      return run === null ? null : (
        <RedoEnd
          run={run}
          failed={redo.banner === 'failed'}
          currentUrl={null}
          doneTitle={messages.doneTitle}
          failedBody={messages.failedBody}
          canRetry={redo.state?.outOfDate === true}
          onClose={redo.closeEnd}
          onRetry={onRedo}
          testIdPrefix="date-redo"
        />
      );
    case 'hidden':
      return redo.showsQuietLine ? <Quiet redo={redo} onRedo={onRedo} /> : null;
  }
};

export const DateRedoBanner = TypedMemo(DateRedoBannerComponent);
